import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authService, ensureSession } from '../services/api'

export interface User {
  id: number | string
  username: string
  fullName: string
  email?: string
  phoneNumber?: string
  phone?: string
  role: 'Owner' | 'Technician'
  isActive: boolean
  createdAt?: string
}

export interface AuthState {
  user: User | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null

  login: (username: string, password: string) => Promise<void>
  logout: () => void
  checkAuth: () => Promise<void>
  clearError: () => void
  setUser: (user: User) => void
}

export const useStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,

      login: async (username: string, password: string) => {
        set({ isLoading: true, error: null })
        try {
          const response = await authService.login(username, password)
          const payload = response?.data ?? response?.Data ?? response

          set({
            user: payload.user,
            token: payload.accessToken,
            refreshToken: payload.refreshToken,
            isAuthenticated: !!payload.accessToken && !!payload.refreshToken && !!payload.user,
            isLoading: false,
            error: null,
          })
        } catch (error: any) {
          set({
            isLoading: false,
            error: error?.response?.data?.message || error.message || 'فشل تسجيل الدخول',
          })
          throw error
        }
      },

      logout: () => {
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        })
      },

      checkAuth: async () => {
        const { refreshToken } = get()

        if (!refreshToken) {
          set({ isAuthenticated: false, isLoading: false })
          return
        }

        set({ isLoading: true })
        const ok = await ensureSession()
        set({
          isAuthenticated: ok,
          isLoading: false,
          error: null,
          ...(ok
            ? {}
            : { user: null, token: null, refreshToken: null }),
        })
      },

      clearError: () => {
        set({ error: null })
      },

      setUser: (user: User) => {
        set({ user })
      },
    }),
    {
      name: 'pos-auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
)
